/*
  <article-toc> — builds an auto table of contents from the article body headings
  and highlights the current section on scroll (scroll-spy). Progressive
  enhancement: the host stays [hidden] until JS finds at least two headings, so
  no-JS visitors never see an empty box. Respects prefers-reduced-motion.
*/
(() => {
  if (customElements.get('article-toc')) return;

  const slugify = (text, used) => {
    let base =
      (text || 'section')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || 'section';
    let slug = base;
    let i = 2;
    while (used.has(slug)) slug = `${base}-${i++}`;
    used.add(slug);
    return slug;
  };

  class ArticleToc extends HTMLElement {
    connectedCallback() {
      const content = document.querySelector(this.getAttribute('data-content') || '.article__content');
      const list = this.querySelector('[data-toc-list]');
      if (!content || !list) return;

      const headings = Array.from(content.querySelectorAll('h2, h3'));
      if (headings.length < 2) {
        this.hidden = true;
        return;
      }

      const used = new Set();
      const links = [];
      headings.forEach((h) => {
        if (h.id) used.add(h.id);
        else h.id = slugify(h.textContent, used);

        const li = document.createElement('li');
        li.className = `article-toc__item article-toc__item--${h.tagName.toLowerCase()}`;
        const a = document.createElement('a');
        a.className = 'article-toc__link';
        a.href = `#${h.id}`;
        a.textContent = h.textContent;
        li.appendChild(a);
        list.appendChild(li);
        links.push(a);
      });

      this.hidden = false;

      // Scroll-spy: highlight the topmost heading inside the reading band.
      let activeId = null;
      const setActive = (id) => {
        if (id === activeId) return;
        activeId = id;
        links.forEach((a) => a.classList.toggle('is-active', a.getAttribute('href').slice(1) === id));
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((e) => e.isIntersecting)
              .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (visible.length) setActive(visible[0].target.id);
          },
          { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
        );
        headings.forEach((h) => io.observe(h));
      }
      setActive(headings[0].id);

      // Smooth in-page navigation that moves focus for accessibility.
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.addEventListener('click', (e) => {
        const link = e.target.closest('a.article-toc__link');
        if (!link) return;
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', `#${target.id}`);
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    }
  }

  customElements.define('article-toc', ArticleToc);
})();
